import { useLocalSearchParams, useNavigation } from 'expo-router';
import { useLayoutEffect } from 'react';
import { FlatList, ListRenderItem, RefreshControl, StyleSheet, View } from 'react-native';

import { useGetAsksQuestions } from '@/api/hooks/questionAnswerHooks';
import { QAResponse } from '@/api/models/questionAnswerModels';
import GoBackButton from '@/components/buttons/GoBackButton';
import DotsLoader from '@/components/DotsLoader';
import Text from '@/components/Text';
import Title from '@/components/Title';
import { useColor } from '@/hooks/useColor';
import { useOnRefresh } from '@/hooks/useOnRefresh';
import { i18n } from '@/utils/i18n';

const QuestionsList = () => {
  const colors = useColor();
  const navigation = useNavigation();
  const { npoId, name } = useLocalSearchParams<{ npoId: string; name: string }>();
  const { data, isLoading, refetch } = useGetAsksQuestions({ npoId });
  const { onDebouncedRefreshing, refreshing } = useOnRefresh(refetch);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerShadowVisible: false,
      headerBackVisible: false,
      headerTitle: () => <Title label={name} />,
      headerLeft: () => <GoBackButton />
    });
  }, [name, navigation]);

  const QuestionItem: ListRenderItem<QAResponse> = ({ item }) => {
    return (
      <>
        <View style={styles.row}>
          <Text style={[styles.item, { color: colors.titleText }]} fontWeight="700">
            {i18n.t('question.question_list.question')}
          </Text>
          <Text style={[styles.value, { color: colors.titleText }]}>{item.question}</Text>
        </View>
        <View style={styles.row}>
          <Text style={[styles.item, { color: colors.titleText }]} fontWeight="700">
            {i18n.t('question.question_list.answer')}
          </Text>
          <Text style={[styles.value, { color: colors.titleText }]}>{item.answer}</Text>
        </View>
      </>
    );
  };

  return (
    <View style={styles.container}>
      {!isLoading && data ? (
        <FlatList
          style={styles.flatList}
          contentContainerStyle={styles.listContainer}
          renderItem={QuestionItem}
          data={data}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onDebouncedRefreshing}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          }
          ListEmptyComponent={<Text style={styles.emptyText}> {i18n.t('mix.n_a')}</Text>}
        />
      ) : (
        <DotsLoader />
      )}
    </View>
  );
};

export default QuestionsList;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },
  loadingContainer: {
    alignItems: 'center'
  },
  flatList: { width: '100%' },
  listContainer: {
    paddingHorizontal: 16,
    paddingTop: 25,
    rowGap: 25
  },
  row: {
    flexDirection: 'row'
  },
  item: {
    minWidth: 70
  },
  value: {
    flex: 1
  },
  emptyText: {
    textAlign: 'center'
  }
});
